import { createRouter } from "vue-router";
import AuthLayout from "@/layouts/Auth/AuthLayout.vue";
import MspLayout from "@/layouts/Msp/MspLayout.vue";
import CustomerLayout from "@/layouts/Customer/CustomerLayout.vue";
import SystemLayout from "@/layouts/System/SystemLayout.vue";
import mspRoutes from "./routes/mspRoutes";
import authRoutes from "./routes/authRoutes";
import systemRoutes from "./routes/systemRoutes";
import ProfileLayout from "@/layouts/Profile/ProfileLayout.vue";
import profileRoutes from "./routes/profileRoutes";
import { createWebHistory } from "vue-router";
import customerRoutes from "./routes/customerRoutes";
import { useAuthenticationStore } from "@/store/authentication.store";
import { $userPrefs } from "@/plugins/userPrefs";
import { isTokenValid } from "@/utils/auth";
import { useAuth0SessionStore } from "@/store/auth0Session.store";
// Imported from the module, not the "@/authorisation" barrel, to keep the
// wrapper component out of the router's module graph.
import { useAuthorisationStore } from "@/authorisation/authorisation.store";
import { resolveAuth0Landing } from "@/utils/auth0";
import { embeddedCompanyLanding } from "@/remote/companyLanding";
import { isEmbedded } from "@/remote/contract";
import { getFlagsConfig } from "@/config/flagsConfig";
import { UUID_SEGMENT_PATTERN, UUID_SEGMENT_REGEX } from "@/constants/regex";
import { getBaseUrl } from "@/config/runtimeConfig";
import { isShellOwnedRoute } from "./shellOwned";
import { installOrgScopeGuard } from "./orgScopeGuard";

export { isShellOwnedRoute } from "./shellOwned";

const isValidUuid = (value) =>
  typeof value === "string" && UUID_SEGMENT_REGEX.test(value);

// Guards can run before pinia is installed (early boot / tests); fall back to
// an empty auth context rather than throwing.
const getAuth = () => {
  try {
    return useAuthenticationStore();
  } catch {
    return {};
  }
};

const getOrgUuid = () => {
  const auth = getAuth();
  const candidate =
    auth.selectedCustomer?.uuid ||
    auth.selectedMsp?.uuid ||
    auth.selectedAccount?.uuid ||
    null;
  return isValidUuid(candidate) ? candidate : null;
};

const normalizeSuffix = (fullPath, legacyBase) => {
  if (!fullPath.startsWith(legacyBase)) return "";
  const suffix = fullPath.slice(legacyBase.length);
  return suffix.startsWith("/") ? suffix : suffix ? `/${suffix}` : "";
};

const buildScopedRedirect = (orgType, to, overrideSuffix = null) => {
  const orgUuid = getOrgUuid();
  if (!orgUuid) {
    return null;
  }

  const targetPath = `/${orgType}/${orgUuid}${overrideSuffix ?? normalizeSuffix(to.fullPath, `/${orgType}`)}`;

  if (targetPath === to.path) return null;

  return {
    path: targetPath,
    query: to.query,
    hash: to.hash,
  };
};

const TOUR_ORG_PATTERN =
  /\/(msp|customer)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?=\/|$)/i;

const getTourOrgBase = () => {
  const referrerMatch = document.referrer.match(TOUR_ORG_PATTERN);
  if (referrerMatch) return `/${referrerMatch[1]}/${referrerMatch[2]}`;

  const auth = getAuth();
  if (isValidUuid(auth.selectedCustomer?.uuid)) {
    return `/customer/${auth.selectedCustomer.uuid}`;
  }
  const mspUuid = [auth.selectedMsp?.uuid, auth.selectedAccount?.uuid].find(
    (uuid) => isValidUuid(uuid),
  );
  return mspUuid ? `/msp/${mspUuid}` : null;
};

const buildTourRedirect = (to, suffix) => {
  const orgBase = getTourOrgBase();
  if (!orgBase) {
    return { name: "auth-sign-in", query: { redirect: to.fullPath } };
  }
  return { path: `${orgBase}${suffix}` };
};

const routes = [
  {
    path: "/",
    redirect: {
      name: "auth-sign-in",
    },
  },
  {
    path: "/go/mail-flow",
    redirect: (to) =>
      buildTourRedirect(to, "/email_security/configuration/mail_flow/settings"),
  },
  {
    path: "/go/block-list",
    redirect: (to) =>
      buildTourRedirect(to, "/email_security/configuration/block_list"),
  },
  {
    path: "/go/geoblocking",
    redirect: (to) =>
      buildTourRedirect(to, "/email_security/configuration/geoblocking"),
  },
  // Legacy single paths → scoped equivalents
  {
    path: "/msp/dashboard",
    redirect: (to) =>
      buildScopedRedirect("msp", to, "/dashboard") || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
      },
  },
  {
    path: "/customer/dashboard",
    redirect: (to) =>
      buildScopedRedirect("customer", to, "/dashboard") || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
      },
  },
  {
    path: "/system/dashboard",
    redirect: (to) =>
      buildScopedRedirect("system", to, "/dashboard") || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
      },
  },
  {
    path: "/customer/email_security",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      return {
        path: `/customer/${orgUuid}/email_security/overview`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/customer/email_security/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/customer/${orgUuid}/email_security${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  // Mis-scoped MSP configuration fallback (missing email_security segment)
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})/configuration`,
    redirect: (to) => ({
      path: `/msp/${to.params.orgUuid}/email_security/configuration`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})/configuration/:rest(.*)`,
    redirect: (to) => ({
      path: `/msp/${to.params.orgUuid}/email_security/configuration/${to.params.rest}`,
      query: to.query,
      hash: to.hash,
    }),
  },
  {
    path: "/customer/settings",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      return {
        path: `/customer/${orgUuid}/settings/administrators`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/customer/settings/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/customer/${orgUuid}/settings${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/system/settings",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      return {
        path: `/system/${orgUuid}/settings/saml-sso`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/system/settings/:rest(.*)",
    redirect: (to) => {
      const orgUuid = getOrgUuid();
      if (!orgUuid)
        return { name: "auth-sign-in", query: { redirect: to.fullPath } };
      const rest = to.params.rest ? `/${to.params.rest}` : "";
      return {
        path: `/system/${orgUuid}/settings${rest}`,
        query: to.query,
        hash: to.hash,
      };
    },
  },
  {
    path: "/auth",
    component: AuthLayout,
    children: authRoutes,
  },

  {
    path: "/sso/callback",
    name: "ssoCalback",
    component: () => import("@/views/Auth/Sso"),
  },
  {
    path: "/azure/callback",
    name: "azureCalback",
    component: () => import("@/views/Auth/Callback"),
  },
  {
    path: "/ndr/callback",
    name: "ndrCallback",
    component: () => import("@/views/Auth/NdrCallback"),
  },
  {
    path: "/logout",
    name: "logout",
    component: () => import("@/views/Auth/Logout"),
  },
  // New scoped bases
  {
    path: `/msp/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: MspLayout,
    name: "msp-root",
    children: mspRoutes,
  },
  // Legacy MSP paths without orgUuid -> redirect to scoped equivalent
  {
    path: "/msp/:rest(.*)",
    redirect: (to) =>
      buildScopedRedirect("msp", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      },
  },
  {
    path: `/customer/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: CustomerLayout,
    children: customerRoutes,
  },
  {
    path: `/system/:orgUuid(${UUID_SEGMENT_PATTERN})`,
    component: SystemLayout,
    children: systemRoutes,
  },

  // Legacy bases redirect to scoped versions (orgUuid inferred later)
  {
    path: "/msp",
    redirect: (to) => ({
      ...(buildScopedRedirect("msp", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: "/customer",
    redirect: (to) => ({
      ...(buildScopedRedirect("customer", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: "/system",
    redirect: (to) => ({
      ...(buildScopedRedirect("system", to) || {
        name: "auth-sign-in",
        query: { redirect: to.fullPath },
        hash: to.hash,
      }),
    }),
  },
  {
    path: `/:organisationUuid(${UUID_SEGMENT_PATTERN})/:rest(.*)?`,
    name: "org-short-link",
    component: () => import("@/views/Auth/OrgRedirect/index.vue"),
  },
  {
    path: "/profile",
    component: ProfileLayout,
    children: profileRoutes,
  },
  {
    path: "/:catchAll(.*)",
    redirect: (to) => ({
      name: "auth-sign-in",
      query: { redirect: to.fullPath },
    }),
  },
];

/**
 * History base, settable at runtime (spec item 5).
 *
 * Standalone this is `import.meta.env.BASE_URL`, fixed when this repo builds.
 * Embedded, the app lives under a host-owned mount path (e.g. `/phish`) that is
 * unknowable at build time — and nav item ids are absolute (`scopeId` builds
 * `/customer/:uuid/...`), so `createWebHistory(base)` is what makes them resolve
 * under the prefix.
 */
let runtimeBase = null;

/**
 * Override the history base before the router is built. The federated entry
 * calls this from its register function; standalone never does.
 *
 * Throws once the router exists rather than silently having no effect: mounting
 * on the wrong base is the kind of failure that shows up later as a blank page
 * after the first `pushState`.
 */
export function setRouterBase(base) {
  if (routerInstance) {
    throw new Error(
      "setRouterBase() was called after the router was already created — set the base before importing the app.",
    );
  }
  // "/phish/" and "/phish" must yield the same base, or paths get a double slash.
  runtimeBase = base ? base.replace(/\/+$/, "") : base;
}

/**
 * Annotated because this is a .js module: without it TypeScript infers `any`
 * from the `null` initialiser, and every consumer of `getRouter()` — including
 * the test suites — silently loses `Router` typing.
 *
 * @type {import("vue-router").Router | null}
 */
let routerInstance = null;

/**
 * The application router, built on first use.
 *
 * Lazily constructed so `setRouterBase()` above can still take effect — the old
 * module-scope `createRouter()` call fixed the base at import time, which a host
 * cannot get ahead of.
 *
 * @returns {import("vue-router").Router}
 */
export function getRouter() {
  if (!routerInstance) {
    routerInstance = createRouter({
      history: createWebHistory(runtimeBase ?? getBaseUrl()),
      linkActiveClass: "active",
      routes,
    });

    // Registered here rather than at module scope: there is no router to guard
    // until this point. See `installNavigationGuard` at the bottom of the file.
    installNavigationGuard(routerInstance);
  }

  return routerInstance;
}

const publicRouteNames = [
  "auth-sign-in",
  "auth-password-reset",
  "auth-password-set",
  "ssoCalback",
  "azureCalback",
  "ndrCallback",
  "logout",
];

// Auth0 RWA cookie session is optional and coexists with the legacy JWT flow.
// Gated behind the AUTH0_LOGIN feature flag so the entire fallback stays inert
// when disabled. Flags are read defensively (getFlagsConfig throws before the
// runtime config loads, and Pinia may be inactive in isolated unit tests), so
// any failure simply resolves to "no Auth0 session".
const isAuth0LoginEnabled = () => {
  try {
    return getFlagsConfig().AUTH0_LOGIN === true;
  } catch {
    return false;
  }
};

const getAuth0Session = () => {
  if (!isAuth0LoginEnabled()) return null;
  try {
    return useAuth0SessionStore();
  } catch {
    return null;
  }
};

/**
 * Load the RBAC policy for an Auth0 session. The legacy `authoriseOnSignIn`
 * call sites (sign-in view, SAML callback, token refresh) all hang off the JWT
 * flow, so an Auth0 session would otherwise leave the policy null — and
 * `hasPermission` denies every write against a null policy, which presents as a
 * read-only UI. The request is cookie-authenticated: `$http` sends credentials
 * and the policy endpoint is same-origin.
 *
 * Never throws: authorisation must not be able to strand navigation. A failure
 * leaves the user with the read-only view they would have had anyway.
 */
const hydrateAuth0Authorisation = async () => {
  try {
    await useAuthorisationStore().ensurePolicy();
  } catch {
    // Pinia inactive (early boot / isolated tests), or an unexpected rejection.
  }
};

/**
 * Fetch the Auth0 user profile (cached after the first call) and mirror it into
 * the legacy auth state. Without this the app has no identity for an Auth0
 * session: the profile menu renders blank, `getOrgUuid()` cannot resolve scoped
 * redirects, and `/profile/security?id=` gets `undefined`.
 * `setAuth0Profile` is called optionally — `getAuth()` returns `{}` when Pinia
 * is inactive.
 */
const hydrateAuth0Identity = async (session) => {
  const profile = await session.ensureProfile();
  if (profile) getAuth().setAuth0Profile?.(profile);
  await hydrateAuth0Authorisation();
  return profile;
};

/**
 * Install the auth navigation guard. Called by `getRouter()` on construction —
 * it used to run at module scope, which is no longer possible now that the
 * router is built lazily so its base can be set at runtime.
 */
function installNavigationGuard(router) {
  router.beforeEach(async (to, _from, next) => {
    // `/` resolves through sign-in to whichever landing this account (or the host's company) has,
    // so a blocked deep link ends up somewhere real instead of nowhere.
    if (isEmbedded() && isShellOwnedRoute(to)) {
      return next({ path: "/" });
    }

    if (to.name && publicRouteNames.includes(to.name)) {
      const token =
        getAuth().token?.access_token || $userPrefs.getCurrentToken();

      // If user has valid token and tries to access auth pages, redirect to landing page
      if (isTokenValid(token) && to.name === "auth-sign-in") {
        // Embedded, the host's company wins over this account's own landing. No-op standalone.
        const hosted = await embeddedCompanyLanding();
        if (hosted) return next({ path: hosted });

        const orgUuid = getOrgUuid();
        const accountType = getAuth().selectedAccount?.type_name;

        if (orgUuid) {
          if (accountType === "System") {
            return next({ path: `/system/${orgUuid}/dashboard` });
          }
          if (accountType === "MSP") {
            return next({ path: `/msp/${orgUuid}/dashboard` });
          }
          return next({ path: `/customer/${orgUuid}/dashboard` });
        }
      }

      // If an Auth0 cookie session is active, don't strand the user on sign-in.
      // The session cookie carries no decodable token, so the identity and the
      // org uuid / account type used for routing come from the profile endpoint.
      if (!isTokenValid(token) && to.name === "auth-sign-in") {
        const session = getAuth0Session();
        if (session) {
          const active = await session.ensureSession();
          if (active.authenticated) {
            // Identity first: the landing below reads `selectedAccount`, which this fills in.
            const profile = await hydrateAuth0Identity(session);

            // As in the JWT branch above; see remote/companyLanding.ts.
            const hosted = await embeddedCompanyLanding();
            if (hosted) return next({ path: hosted });

            const landing = resolveAuth0Landing(profile?.organisation);
            if (landing) {
              return next(landing);
            }
          }
        }
      }

      return next();
    }

    const token = getAuth().token?.access_token || $userPrefs.getCurrentToken();

    if (isTokenValid(token)) {
      return next();
    }

    // No valid JWT — fall back to the Auth0 cookie session before bouncing to sign-in.
    const session = getAuth0Session();
    if (session) {
      const active = await session.ensureSession();
      if (active.authenticated) {
        // Hydrate identity here too: a direct page load of a protected route never
        // passes through the sign-in branch above, and the nav needs the user.
        await hydrateAuth0Identity(session);
        return next();
      }
    }

    return next({
      name: "auth-sign-in",
      query: { redirect: to.fullPath },
    });
  });

  installOrgScopeGuard(router);
}

export default {};
